import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { toast } from 'react-toastify';

const STORAGE_KEY = 'spinclean-settings';

const DEFAULT_SETTINGS = {
  business: {
    businessName: 'Laundry Management',
    ownerName: 'Dana Lee',
    email: 'admin@laundrymanagement.com',
    phone: '555-0001',
    address: '100 Executive Blvd, New York, NY 10001',
    gstNumber: 'GST-29ABCDE1234F1Z5',
    website: 'https://laundrymanagement.com',
    logo: '',
  },
  system: {
    currency: 'KWD',
    timezone: 'Asia/Kuwait',
    dateFormat: 'DD/MM/YYYY',
    defaultDeliveryTime: '48 hours',
  },
  notifications: {
    emailAlerts: true,
    smsAlerts: false,
    orderUpdates: true,
    paymentReminders: true,
    pickupDeliveryAlerts: true,
    marketingEmails: false,
  },
  security: {
    twoFactorAuth: false,
    sessionTimeout: '30',
    loginAlerts: true,
  },
  payment: {
    upiQrCode: '',
  },
};

const SettingsContext = createContext();

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (saved) {
        return {
          ...DEFAULT_SETTINGS,
          ...saved,
          business: {
            ...DEFAULT_SETTINGS.business,
            ...saved.business,
            businessName: DEFAULT_SETTINGS.business.businessName,
          },
          isLoaded: true,
        };
      }
    } catch (_) {}
    return { ...DEFAULT_SETTINGS, isLoaded: true };
  });
  const settingsRef = useRef(settings);

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  const updateSection = async (section, data) => {
    const updatedSection = { ...settings[section], ...data };
    const nextSettings = {
      ...settings,
      [section]: updatedSection,
      isLoaded: true
    };

    setSettings(nextSettings);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(nextSettings));

    toast.success('Settings updated successfully');
  };

  const resetSection = async (section) => {
    const nextSettings = {
      ...settings,
      [section]: { ...DEFAULT_SETTINGS[section] },
      isLoaded: true
    };

    setSettings(nextSettings);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(nextSettings));

    toast.success('Section settings reset');
  };

  const resetAll = async () => {
    const nextSettings = { ...DEFAULT_SETTINGS, isLoaded: true };
    setSettings(nextSettings);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_SETTINGS));

    toast.success('All settings reset to defaults');
  };

  const changePassword = async (currentPassword, newPassword) => {
    if (!currentPassword || !newPassword) {
      toast.error('Please enter both passwords');
      return false;
    }
    toast.success('Password changed successfully for this browser session');
    return true;
  };

  const logout = () => {
    localStorage.removeItem('user');
    toast.success('Logged out successfully');
  };

  return (
    <SettingsContext.Provider value={{ settings, updateSection, resetSection, resetAll, changePassword, logout }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider');
  return ctx;
};

export { DEFAULT_SETTINGS };
